import { normalizeScoreTo } from './normalize-score-to';
import type { GradientStop, TScoreColorScale } from '../types';
import { APP_SCORING_PERCENTAGE_RANGES } from '@/constants';
import { TNormalizedRatingCutoffs } from '@/types';

/**
 * Returns gradient stops for role-play mode based on normalized score bands.
 */
export const getRolePlayGradientStops = (
  scoreValue: number,
  colorScale: TScoreColorScale,
  runtimeRatingCutoffs?: TNormalizedRatingCutoffs,
): GradientStop[] => {
  // Normalize the score to a percentage for consistent band thresholds.
  const normalizedValue = normalizeScoreTo(scoreValue, 'percent');
  const ranges = runtimeRatingCutoffs ?? APP_SCORING_PERCENTAGE_RANGES;

  const l1Upper = ranges.L1[1];
  const l2Upper = ranges.L2[1];
  const l3Upper = ranges.L3[1];

  const safeRatio = (threshold: number) => {
    if (normalizedValue <= 0) return 0;

    return Math.max(0, Math.min(1, threshold / normalizedValue));
  };

  // Band 1: 0% to 20% - solid L1 color
  if (normalizedValue <= l1Upper) {
    return [
      { offset: 0, color: colorScale[1] },
      { offset: 1, color: colorScale[1] },
    ];
  }

  // Band 2: >21% to 50% - gradient from L1 to L2
  if (normalizedValue <= l2Upper) {
    return [
      { offset: 0, color: colorScale[1] },
      { offset: safeRatio(l1Upper), color: colorScale[1] },
      { offset: 1, color: colorScale[2] },
    ];
  }

  // Band 3: >51% to 89% - gradient from L1 to L2 to L3
  if (normalizedValue <= l3Upper) {
    return [
      { offset: 0, color: colorScale[1] },
      { offset: safeRatio(l1Upper), color: colorScale[2] },
      { offset: safeRatio(l2Upper), color: colorScale[3] },
      { offset: 1, color: colorScale[3] },
    ];
  }

  // Band 4: >90% - gradient from L1 to L2 to L3 to L4
  return [
    { offset: 0, color: colorScale[1] },
    { offset: safeRatio(l1Upper), color: colorScale[2] },
    { offset: safeRatio(l2Upper), color: colorScale[3] },
    { offset: safeRatio(l3Upper), color: colorScale[4] },
    { offset: 1, color: colorScale[4] },
  ];
};
